import { useState, useRef, useCallback } from 'react'
import { api } from '../lib/api'
import type { Recording } from '../lib/types'

interface Props {
  companyId: string
  recordings: Recording[]
  onRecordingSaved: (recording: Recording) => void
}

export default function AudioRecorder({ companyId, recordings, onRecordingSaved }: Props) {
  const [recording, setRecording] = useState(false)
  const [duration, setDuration] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const mediaRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval>>()
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' })
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop())
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
        const filename = `recording-${timestamp}.webm`

        setUploading(true)
        try {
          const saved = await api.uploadRecording(companyId, blob, filename)
          onRecordingSaved(saved)
        } catch (err) {
          console.error('Upload failed:', err)
        } finally {
          setUploading(false)
        }
      }

      mediaRef.current = recorder
      recorder.start(1000)
      setRecording(true)
      setDuration(0)
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000)
    } catch (err) {
      alert('Microphone access denied. Please allow microphone access to record.')
    }
  }, [companyId, onRecordingSaved])

  const stopRecording = useCallback(() => {
    if (mediaRef.current && mediaRef.current.state !== 'inactive') {
      mediaRef.current.stop()
    }
    if (timerRef.current) clearInterval(timerRef.current)
    setRecording(false)
  }, [])

  const playRecording = async (rec: Recording) => {
    if (playingId === rec.id) {
      audioRef.current?.pause()
      setPlayingId(null)
      return
    }
    try {
      const url = await api.getRecordingUrl(rec.id)
      if (audioRef.current) audioRef.current.pause()
      const audio = new Audio(url)
      audio.onended = () => setPlayingId(null)
      audioRef.current = audio
      audio.play()
      setPlayingId(rec.id)
    } catch (err) {
      console.error('Playback failed:', err)
    }
  }

  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return ''
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="font-semibold mb-3">Record Conversation</h3>
        <p className="text-sm text-gray-500 mb-4">
          Record your booth conversation for later review. Your phone microphone captures audio while
          you talk with the vendor.
        </p>

        <div className="flex items-center gap-4">
          {recording ? (
            <>
              <button onClick={stopRecording} className="flex items-center gap-2 bg-red-600 text-white px-5 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors">
                <span className="w-3 h-3 bg-white rounded-sm" />
                Stop Recording
              </button>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-red-500 rounded-full recording-pulse" />
                <span className="font-mono text-lg">{formatDuration(duration)}</span>
              </div>
            </>
          ) : (
            <button onClick={startRecording} disabled={uploading} className="flex items-center gap-2 btn-primary py-3">
              <span className="w-3 h-3 bg-white rounded-full" />
              {uploading ? 'Uploading...' : 'Start Recording'}
            </button>
          )}
        </div>
      </div>

      {recordings.length > 0 && (
        <div className="card">
          <h3 className="font-semibold mb-3">Previous Recordings ({recordings.length})</h3>
          <div className="divide-y">
            {recordings.map(rec => (
              <div key={rec.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => playRecording(rec)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-100 text-brand-600 hover:bg-brand-200 transition-colors"
                  >
                    {playingId === rec.id ? '⏸' : '▶'}
                  </button>
                  <div>
                    <p className="text-sm font-medium">{rec.filename}</p>
                    <p className="text-xs text-gray-400">
                      {rec.duration_seconds ? formatDuration(rec.duration_seconds) : ''}
                      {rec.size_bytes ? ` · ${formatFileSize(rec.size_bytes)}` : ''}
                      {' · '}{new Date(rec.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
